import React, { useState } from "react";
import { useNavigate } from "react-router";
import {
  DashboardSegment,
  DashboardSegmentRow,
  getSegment,
} from "../../../../services/dashboardService";
import { getSubscriptionStatusLabel } from "../../../../utils/subscriptionStatusLabel";
import Loading from "../../../../components/Loading";
import Pagination from "../../../../components/Pagination";

interface SegmentSectionProps {
  segment: DashboardSegment;
  title: string;
  description?: string;
}

const formatDateOrDash = (value: string | null): string =>
  value ? new Date(value).toISOString().split("T")[0] : "-";

const SegmentSection: React.FC<SegmentSectionProps> = ({
  segment,
  title,
  description,
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<DashboardSegmentRow[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const loadPage = async (targetPage: number) => {
    setLoading(true);
    const result = await getSegment(segment, targetPage, 20);
    setRows(result.data);
    setPage(result.page);
    setTotalPages(result.totalPages);
    setTotal(result.total);
    setLoading(false);
    setHasLoadedOnce(true);
  };

  const toggleOpen = () => {
    const next = !isOpen;
    setIsOpen(next);
    if (next && !hasLoadedOnce) {
      loadPage(1);
    }
  };

  return (
    <div className="mb-4 bg-white rounded-lg shadow overflow-hidden">
      <button
        onClick={toggleOpen}
        className="w-full flex justify-between items-center px-4 py-3 text-left hover:bg-gray-50"
      >
        <div>
          <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
          {description && (
            <p className="text-xs text-gray-500">{description}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {hasLoadedOnce && (
            <span className="text-xs text-gray-500">
              {total} member{total === 1 ? "" : "s"}
            </span>
          )}
          <span className="text-lg font-bold text-gray-400">
            {isOpen ? "-" : "+"}
          </span>
        </div>
      </button>

      {isOpen && (
        <div className="border-t overflow-x-auto">
          {loading ? (
            <Loading message="Loading..." />
          ) : rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500">
              No members in this segment.
            </p>
          ) : (
            <>
              <table className="min-w-full border-collapse">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="py-2 px-4 text-left">Name</th>
                    <th className="py-2 px-4 text-left">Email</th>
                    <th className="py-2 px-4 text-left">Subscription</th>
                    <th className="py-2 px-4 text-left">Last Login</th>
                    <th className="py-2 px-4 text-left">Last Workout</th>
                    <th className="py-2 px-4 text-left" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-t">
                      <td className="py-3 px-4 text-sm whitespace-nowrap">
                        {row.name || "N/A"}
                      </td>
                      <td className="py-3 px-4 text-sm whitespace-nowrap">
                        {row.email}
                      </td>
                      <td className="py-3 px-4 text-sm whitespace-nowrap">
                        {getSubscriptionStatusLabel(row.subscriptionStatus)}
                      </td>
                      <td className="py-3 px-4 text-sm whitespace-nowrap">
                        {formatDateOrDash(row.lastLoginAt)}
                      </td>
                      <td className="py-3 px-4 text-sm whitespace-nowrap">
                        {formatDateOrDash(row.lastWorkoutAt)}
                      </td>
                      <td className="py-3 px-4 text-sm whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/users/${row.id}`)}
                          className="text-red-600 hover:underline text-xs"
                        >
                          View Profile
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={loadPage}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SegmentSection;
